import { apiFetch } from "../api/client";
import { useTenantScope } from "../scope/TenantScopeContext";
import React, { useCallback, useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL || "https://api.wirelesspress.org";

function profile() {
  for (const key of ["user", "currentUser", "profile"]) {
    try { const value = JSON.parse(localStorage.getItem(key) || "null"); if (value && typeof value === "object") return value; } catch (_) {}
  }
  return {};
}

function headers(json = false) {
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(json ? { "Content-Type": "application/json" } : {}) };
}

async function api(path, options = {}) {
  const response = await apiFetch(`${path}`, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.detail || `Request failed with HTTP ${response.status}`);
  return body;
}

const panel = { background: "#101923", border: "1px solid #263747", borderRadius: 14, padding: 20, marginBottom: 18 };
const input = { width: "100%", boxSizing: "border-box", background: "#0a1118", color: "#eef5fb", border: "1px solid #31485b", borderRadius: 8, padding: "10px 12px" };
const button = { border: 0, borderRadius: 8, padding: "9px 13px", color: "white", background: "#285d7a", cursor: "pointer", fontWeight: 700 };

export default function PolicyAssignmentsPage() {
  const globalScope = useTenantScope();
  const user = useMemo(profile, []);
  const role = user.role || localStorage.getItem("role") || "";
  const initialCompany = user.company_id || localStorage.getItem("company_id") || "";
  const isAdmin = ["super_admin", "company_admin"].includes(role);
  const [companyId, setCompanyId] = useState(initialCompany);
  const [groups, setGroups] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [acks, setAcks] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [memberGroup, setMemberGroup] = useState("");
  const [memberDevice, setMemberDevice] = useState("");
  const [policyUuid, setPolicyUuid] = useState("");
  const [targetType, setTargetType] = useState("device");
  const [targetValue, setTargetValue] = useState("");
  const [effectiveDevice, setEffectiveDevice] = useState("");

  // R12I_ASSIGN_DEVICE_SYNC
  useEffect(() => {
    if (globalScope.deviceUuid) {
      setEffectiveDevice(globalScope.deviceUuid);
    }
  }, [globalScope.deviceUuid]);

  // Consolidated tenant scope is authoritative for super administrators.
  useEffect(() => {
    if (role !== "super_admin") return;

    const scopedCompany = String(
      globalScope.companyId || ""
    );

    if (scopedCompany !== String(companyId || "")) {
      setCompanyId(scopedCompany);
    }
  }, [companyId, globalScope.companyId, role]);

  const [effective, setEffective] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const suffix = role === "super_admin" && companyId ? `?company_id=${companyId}` : "";
  const load = useCallback(async () => {
    if (!isAdmin || (role === "super_admin" && !companyId)) return;
    try {
      setError("");
      const [g, p, a, k] = await Promise.all([
        api(`/policy-assignment/groups${suffix}`, { headers: headers() }),
        api("/policy-lifecycle/policies", { headers: headers() }),
        api(`/policy-assignment/assignments${suffix}`, { headers: headers() }),
        api(`/policy-assignment/acknowledgments${suffix}`, { headers: headers() }),
      ]);
      setGroups(Array.isArray(g) ? g : []); setPolicies(Array.isArray(p) ? p : []);
      setAssignments(Array.isArray(a) ? a : []); setAcks(Array.isArray(k) ? k : []);
      if (!policyUuid && p?.length) setPolicyUuid(p[0].policy_uuid);
    } catch (err) { setError(err.message); }
  }, [companyId, isAdmin, policyUuid, role, suffix]);

  useEffect(() => { load(); }, [load]);

  async function post(path, payload) {
    setBusy(true); setError(""); setNotice("");
    try { const result = await api(path, { method: "POST", headers: headers(true), body: JSON.stringify({ ...payload, ...(role === "super_admin" ? { company_id: Number(companyId) } : {}) }) }); await load(); return result; }
    catch (err) { setError(err.message); return null; }
    finally { setBusy(false); }
  }

  async function createGroup(event) {
    event.preventDefault(); const result = await post("/policy-assignment/groups", { name: groupName, description: "Dashboard-managed device group" });
    if (result) { setNotice("Device group created."); setGroupName(""); }
  }

  async function addMember(event) {
    event.preventDefault(); const result = await post(`/policy-assignment/groups/${memberGroup}/members`, { device_uuid: memberDevice });
    if (result) { setNotice("Device added to group."); setMemberDevice(""); }
  }

  async function removeMember() {
    if (!memberGroup || !memberDevice) { setError("Select a group and enter a device UUID first."); return; }
    const result = await post(`/policy-assignment/groups/${memberGroup}/members/${memberDevice}/retire`, {});
    if (result) { setNotice("Device membership retired; its history remains preserved."); setMemberDevice(""); }
  }

  async function assign(event) {
    event.preventDefault();
    const target = targetType === "device" ? { device_uuid: targetValue } : targetType === "group" ? { group_uuid: targetValue } : {};
    const result = await post("/policy-assignment/assignments", { policy_uuid: policyUuid, target_type: targetType, ...target, reason: "Dashboard policy assignment" });
    if (result) setNotice("Published policy version assigned. Enforcement depends on the effective policy mode, agent acknowledgment, current device capabilities, kill switch state, and platform verification.");
  }

  async function resolveEffective(event) {
    event.preventDefault();
    try { setError(""); const query = role === "super_admin" ? `?company_id=${companyId}` : ""; setEffective(await api(`/policy-assignment/effective/${effectiveDevice}${query}`, { headers: headers() })); }
    catch (err) { setError(err.message); }
  }

  return <main style={{ color: "#eef5fb", padding: 24, maxWidth: 1400, margin: "0 auto" }}>
    <h1 style={{ marginBottom: 6 }}>Policy Assignments</h1>
    <p style={{ color: "#a9bbca", marginTop: 0 }}>Assign immutable published policy versions to a company, group, or device. Precedence is device &gt; group &gt; company. Acknowledgment means received, not enforced.</p>
    {error && <div style={{ ...panel, borderColor: "#b42318", color: "#ffb4ab" }}>{error}</div>}
    {notice && <div style={{ ...panel, borderColor: "#16815d", color: "#9ce4c8" }}>{notice}</div>}
    {!isAdmin && <section style={panel}>Company administrator access is required.</section>}
    {isAdmin && <>
      {role === "super_admin" && <section style={panel}><strong>Tenant scope</strong><div style={{ color: "#a9bbca", marginTop: 6 }}>{companyId ? `Company ${companyId}` : "Select a company from the global scope control."}</div></section>}
      <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 18 }}>
        <form onSubmit={createGroup} style={panel}><h2>Create device group</h2><input required value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Group name" style={input} /><button disabled={busy} style={{ ...button, marginTop: 12 }}>Create group</button></form>
        <form onSubmit={addMember} style={panel}><h2>Manage group membership</h2><select required value={memberGroup} onChange={(e) => setMemberGroup(e.target.value)} style={input}><option value="">Select group</option>{groups.filter((g) => g.status === "active").map((g) => <option key={g.group_uuid} value={g.group_uuid}>{g.name}</option>)}</select><input required value={memberDevice} onChange={(e) => setMemberDevice(e.target.value)} placeholder="Device UUID" style={{ ...input, marginTop: 10 }} /><div style={{ display: "flex", gap: 10 }}><button disabled={busy} style={{ ...button, marginTop: 12 }}>Add member</button><button disabled={busy} type="button" onClick={removeMember} style={{ ...button, marginTop: 12, background: "#8b3a3a" }}>Retire membership</button></div></form>
      </section>
      <form onSubmit={assign} style={panel}><h2>Create or replace assignment</h2><div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 }}><select required value={policyUuid} onChange={(e) => setPolicyUuid(e.target.value)} style={input}>{policies.filter((p) => p.published_version_id && p.status === "active").map((p) => <option key={p.policy_uuid} value={p.policy_uuid}>{p.name} · published v{p.published_version_number}</option>)}</select><select value={targetType} onChange={(e) => setTargetType(e.target.value)} style={input}><option value="company">Company default</option><option value="group">Device group</option><option value="device">Single device</option></select>{targetType !== "company" && <input required value={targetValue} onChange={(e) => setTargetValue(e.target.value)} placeholder={targetType === "group" ? "Group UUID" : "Device UUID"} style={input} />}<button disabled={busy} style={button}>Assign pinned version</button></div></form>
      <form onSubmit={resolveEffective} style={panel}><h2>Effective policy resolver</h2><div style={{ display: "flex", gap: 10 }}><input required value={effectiveDevice} onChange={(e) => setEffectiveDevice(e.target.value)} placeholder="Device UUID" style={input} /><button style={button}>Resolve</button></div>{effective && <pre style={{ whiteSpace: "pre-wrap", color: "#9ce4c8" }}>{JSON.stringify(effective, null, 2)}</pre>}</form>
      <section style={panel}><h2>Active and historical assignments</h2>{assignments.map((a) => <div key={a.assignment_uuid} style={{ borderBottom: "1px solid #31485b", padding: "10px 0" }}><strong>{a.policy_name} · v{a.version_number}</strong><div>{a.target_type} · {a.device_uuid || a.group_uuid || a.target_company_id} · {a.status}</div><code>{a.content_sha256}</code></div>)}</section>
      <section style={panel}><h2>Agent receipt acknowledgments</h2>{acks.map((a) => <div key={a.acknowledgment_uuid} style={{ borderBottom: "1px solid #31485b", padding: "10px 0" }}><strong>{a.device_uuid} · received</strong><div>Assignment {a.assignment_uuid} · version {a.version_uuid}</div></div>)}{!acks.length && <p style={{ color: "#a9bbca" }}>No canonical acknowledgments yet.</p>}</section>
    </>}
  </main>;
}
